import * as React from 'react';
import { Check, ChevronsUpDown } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import type { SelectOption } from '@/components/common/SearchableSelect';

export default function MultiSearchableSelect({
  options,
  value,
  onChange,
  minimum = 0,
  placeholder = 'Choose…',
  searchPlaceholder = 'Search…',
  emptyText = 'Nothing matches.',
  disabled,
  testId,
  className,
}: {
  options: SelectOption[];
  value: string[];
  onChange: (value: string[]) => void;
  minimum?: number;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyText?: string;
  disabled?: boolean;
  testId?: string;
  className?: string;
}) {
  const [open, setOpen] = React.useState(false);
  const chosen = options.filter((option) => value.includes(option.value));

  const toggle = (option: SelectOption) => {
    const selected = value.includes(option.value);
    if (selected && value.length <= minimum) return;
    const next = selected ? value.filter((v) => v !== option.value) : [...value, option.value];
    onChange(options.map((o) => o.value).filter((v) => next.includes(v)));
  };

  return (
    <Popover open={open} onOpenChange={setOpen} modal>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={`w-full justify-between font-normal ${chosen.length ? '' : 'text-muted-foreground'} ${className ?? ''}`}
          data-testid={testId}
        >
          <span className="truncate">{chosen.length ? chosen.map((option) => option.label).join(', ') : placeholder}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] min-w-[14rem] p-0" align="start">
        <Command>
          <CommandInput
            placeholder={searchPlaceholder}
            data-testid={testId ? `${testId}-search` : undefined}
          />
          <CommandList>
            <CommandEmpty>{emptyText}</CommandEmpty>
            <CommandGroup>
              {options.map((option) => {
                const selected = value.includes(option.value);
                const locked = selected && value.length <= minimum;
                return (
                  <CommandItem
                    key={option.value}
                    value={`${option.label} ${option.keywords ?? ''}`}
                    disabled={option.disabled}
                    onSelect={() => toggle(option)}
                    className={option.disabled ? 'opacity-50' : undefined}
                    data-testid={testId ? `${testId}-option-${option.value}` : undefined}
                    title={locked ? `At least ${minimum} must stay chosen` : undefined}
                  >
                    <Check className={`mr-2 h-4 w-4 shrink-0 ${selected ? 'opacity-100' : 'opacity-0'}`} />
                    <span className="truncate">{option.label}</span>
                    {option.hint && (
                      <span className="ml-auto pl-2 text-xs text-muted-foreground">{option.hint}</span>
                    )}
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
